import React, { useEffect, useState } from 'react';
import { Bell, UserRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { fetchCurrentUserBookings, getCurrentBookingEmail } from '../lib/bookingService';
import { buildBookingNotification, requestNotificationPermission } from '../lib/notificationService';
import { shouldRequestPushPermission, subscribeAndSavePushSubscription } from '../lib/pushSubscription';

export default function TopBar({ isAuthenticated = false, authLoading = false }) {
  const navigate = useNavigate();
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      setHasUnread(false);
      return undefined;
    }

    if (shouldRequestPushPermission()) {
      requestNotificationPermission()
        .then((granted) => {
          if (granted) {
            return subscribeAndSavePushSubscription();
          }
          return undefined;
        })
        .catch((error) => {
          console.warn('Failed to subscribe for booking push:', error);
        });
    }

    let channel;
    let isSubscribed = true;

    const preloadNotifications = async () => {
      try {
        const { bookings } = await fetchCurrentUserBookings();
        if (!isSubscribed) return;

        const seededNotifications = (bookings || [])
          .map((booking) => buildBookingNotification(booking, { idPrefix: 'seed' }))
          .filter(Boolean)
          .sort((left, right) => right.createdAt.getTime() - left.createdAt.getTime())
          .slice(0, 10);

        setHasUnread(seededNotifications.length > 0);
      } catch (error) {
        console.error('Failed to preload booking notifications:', error);
      }
    };

    const setupNotifications = async () => {
      const email = await getCurrentBookingEmail();

      if (!isSubscribed) return;

      await preloadNotifications();

      if (!email || !isSubscribed) return;

      channel = supabase
        .channel(`bookings:${email}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bookings',
            filter: `requester_email=eq.${email}`,
          },
          (payload) => {
            const booking = payload.new;
            const oldBooking = payload.old || {};
            const message = buildBookingNotification({
              ...booking,
              booking_status: booking.status,
            });

            const changedStatus = String(oldBooking.status || '').toLowerCase() !== String(booking.status || '').toLowerCase();
            const changedStage = String(oldBooking.project_stage || '').toLowerCase() !== String(booking.project_stage || '').toLowerCase();

            if (!message || (!changedStatus && !changedStage)) {
              return;
            }

            setHasUnread(true);
          }
        )
        .subscribe();
    };

    setupNotifications();

    return () => {
      isSubscribed = false;
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [authLoading, isAuthenticated]);

  const handleBellClick = () => {
    setHasUnread(false);
    navigate('/notifications');
  };

  const logoTo = isAuthenticated ? '/dashboard' : '/';

  return (
    <header className="sticky top-0 z-40 bg-[#8b0000] border-b border-black/25">
      <div className="mx-auto max-w-7xl h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to={logoTo} aria-label={isAuthenticated ? 'Go to Event Dashboard' : 'Go to home'} className="inline-flex items-center gap-3">
          <img
            src="/assets/echojham_logos/echojham.logo2.png"
            alt="Echo Jham"
            className="h-10 w-auto"
          />
        </Link>

        <div className="relative min-w-9 min-h-9">
          {authLoading ? null : isAuthenticated ? (
            <button
              type="button"
              onClick={handleBellClick}
              aria-label="Notifications"
              className="relative h-9 w-9 rounded-full border border-white/25 bg-white text-[#8b0000] grid place-items-center hover:bg-white/90 transition"
            >
              <Bell size={16} />
              {hasUnread && (
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
          ) : (
            <Link
              to="/login"
              aria-label="Account login or sign up"
              className="relative h-9 w-9 rounded-full border border-white/25 bg-white text-[#8b0000] grid place-items-center hover:bg-white/90 transition"
            >
              <UserRound size={16} />
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
